import { Component, EventEmitter, Input, OnInit, ViewChild, ViewEncapsulation, ChangeDetectorRef } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Actions, Select, Store } from '@ngxs/store';
import { AllUserDataSource } from 'app/library/all-users/components/active-user-list/datasource';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { User } from 'app/projects/user/src/lib/models/user';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { SortAllUsers } from '../../../../all-users/actions/index';
import { FeatureService } from '../../../../feature/services/feature.service';
import { AllUsersState } from 'app/library/all-users/models/all-users.state';
import { MatStepper } from '@angular/material/stepper';
import { ProgramItemService } from 'app/library/program-item/services/program-item.service';
import { ProgramStructureWithGroup } from 'app/library/program-item/models/program-structure-with-group.model';
import { ParticipantExecutionChange } from 'app/library/program-item/models/participant-execution-change.model';
import { TranslateService } from '@ngx-translate/core';
import { ModuleUserExtended } from 'app/library/module-user/models/module-user.extended.model';
import { Navigate } from '@ngxs/router-plugin';
import { ModuleKind } from 'app/library/module/enums/module-kind.enum';
import { ModuleStatuses } from 'app/library/module/models/module-statuses';
import { CourseRelevantExecution } from 'app/library/relevant-execution/models/course-relevant-execution.model';
import { CourseRelevantExecutionDisabledReasonEnum } from 'app/library/relevant-execution/enums/course-relevant-execution-disabled-reason.enum';

@Component({
    selector: 'app-add-program-participants-dialog',
    templateUrl: './add-program-participants-dialog.component.html',
    styleUrls: ['./add-program-participants-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class AddProgramParticipantsDialogComponent extends UnsubscribeOnDestroy implements OnInit {
    private static readonly BOOKING_CONTEXT = 'booking';
    private static readonly LISTING_REGISTRATION_CLOSED_STATUS = 'closed';

    @Input()
    entitie$: Observable<User[]>;

    @Input()
    entityId: string;

    @Input()
    title: string;

    @Input()
    dataType: string;

    @Input()
    role: string;

    @Input()
    moduleId: string;

    @Input()
    list: User[] = [];

    @Input()
    moduleUser: ModuleUserExtended;

    @Input()
    showFirstStep: boolean = true;

    get shouldShowNextButton(): boolean {
        return this.stepper?.selectedIndex === 0 && this.showFirstStep;
    }

    get shouldShowBookUsersButton(): boolean {
        return (this.stepper?.selectedIndex === 1 && this.showFirstStep) || (this.stepper?.selectedIndex === 0 && !this.showFirstStep);
    }

    get shouldShowSaveButton(): boolean {
        return this.stepper?.selectedIndex === this.stepper?.steps.length - 1;
    }

    @ViewChild(MatStepper) stepper: MatStepper;

    onConfirm = new EventEmitter<User>();

    programStructuresWithGroups: ProgramStructureWithGroup[] = [];

    form: FormGroup;
    dataSource: AllUserDataSource;
    displayedColumns = [];
    removable = true;
    searchInput = new FormControl();
    hideEmail: boolean;
    roleLabel: string;
    selectedExecutionIds: string[] = [];
    bookingsSet = new Set<string>();
    participantsExecutionChanges: ParticipantExecutionChange[] = [];
    isProgramStructuresWithGroupsLoading: boolean = false;
    isCourseCatalogFeatureEnabled: boolean;
    ModuleKind = ModuleKind;

    @Select(AllUsersState.getTotalCount)
    totalCount$: Observable<number>;

    @ViewChild(MatPaginator)
    paginator: MatPaginator;
    @ViewChild(MatSort)
    sort: MatSort;

    sortBy = this._store.selectSnapshot(AllUsersState.getSortBy);
    sortOrder = this._store.selectSnapshot(AllUsersState.getSortOrder);

    constructor(
        private _store: Store,
        public dialogRef: MatDialogRef<AddProgramParticipantsDialogComponent>,
        private _action$: Actions,
        private _featureService: FeatureService,
        private _programItemService: ProgramItemService,
        private _translateService: TranslateService,
        private fb: FormBuilder,
        private _changeDetectorRef: ChangeDetectorRef
    ) {
        super();

        this.hideEmail = this._featureService.checkFeatureStatus(FeatureSwitchName.ParticipantEmailVisibility, 'inactive');
        this.isCourseCatalogFeatureEnabled = this._featureService.checkFeatureStatus(FeatureSwitchName.CourseCatalog);
    }

    async ngOnInit(): Promise<void> {
        this.searchInput.valueChanges.pipe(takeUntil(this._unsubscribeAll), debounceTime(1000), distinctUntilChanged()).subscribe((searchText) => {
            this.loadUsersPage(true);
        });

        this.initForm();
        this.loadData();

        this.form.valueChanges.subscribe(() => {
            const controlArray = this.form.get('structures') as FormArray;
            this.selectedExecutionIds = controlArray.controls.filter((control) => control.value !== null).map((control) => control.value);
        });

        if (this.list.length) {
            this.dataSource = new AllUserDataSource(
                this.entitie$.pipe(
                    map((users) => {
                        return users?.map((item) => {
                            if (this.list.find((item2) => item2.id === item.id)) {
                                item = Object.assign(new User(), item, {
                                    selected: true,
                                });
                            }
                            return item;
                        });
                    })
                ),
                this._store,
                this._action$
            );
        } else {
            this.dataSource = new AllUserDataSource(this.entitie$, this._store, this._action$);
        }

        if (this.showFirstStep) {
            setTimeout(() => {
                if (this.sortBy && this.sortOrder) {
                    this.sort.sort({
                        id: this.sortBy || '',
                        start: this.sortOrder || 'asc',
                        disableClear: false,
                    });
                }

                this.loadUsersPage(false);

                this.sort.sortChange.pipe(takeUntil(this._unsubscribeAll)).subscribe((sort) => {
                    this._store.dispatch(new SortAllUsers(sort));

                    this.loadUsersPage(true);
                });

                this.paginator.page.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
                    this.loadUsersPage(false);
                });

                if (this.hideEmail) {
                    this.displayedColumns = ['avatar', 'firstName', 'lastName', 'buttons'];
                } else {
                    this.displayedColumns = ['avatar', 'firstName', 'lastName', 'email', 'buttons'];
                }
            });
        }
    }

    initForm(): void {
        this.form = this.fb.group({
            structures: this.fb.array([]),
        });
    }

    prepareExecutionOptionsLoad(i: number, j: number): void {
        const structure = this.programStructuresWithGroups[i].structure[j];

        if (!structure.hasLoadedOptions && !structure.isLoading) {
            structure.isLoading = true;
            this._changeDetectorRef.detectChanges();
        }
    }

    async onFormControlClick(i: number, j: number, isOpened: boolean): Promise<void> {
        const structure = this.programStructuresWithGroups[i].structure[j];

        if (!isOpened) {
            if (!structure.hasLoadedOptions) {
                structure.isLoading = false;
            }
            return;
        }

        if (!structure.hasLoadedOptions && !structure.isLoading) {
            structure.isLoading = true;
            this._changeDetectorRef.detectChanges();
        }

        const options = await this._programItemService.fetchProgramExecutionCourseOfferingRelevantExecutions(
            this.entityId,
            structure.courseOffering.id,
            this.getRelevantExecutionsContext()
        );
        structure.options = options;
        structure.hasLoadedOptions = true;
        const selectedControl = this.getFormControl(i, j);
        const selectedOption = options?.find((option) => option.id === selectedControl?.value) as CourseRelevantExecution;

        // Drop previously selected executions that are now blocked in booking context.
        if (selectedOption && !this.isExecutionSelectable(selectedOption)) {
            selectedControl?.setValue(null);
        }
        structure.isLoading = false;
        this._changeDetectorRef.detectChanges();
    }

    isExecutionSelectable(option: CourseRelevantExecution | null): boolean {
        if (!option) {
            return true;
        }

        if (option.selectionStatus) {
            return option.selectionStatus.isSelectable;
        }

        const status = option.status?.toLowerCase();
        return status !== ModuleStatuses.PLANNED && status !== AddProgramParticipantsDialogComponent.LISTING_REGISTRATION_CLOSED_STATUS;
    }

    getDisabledExecutionSuffixLabel(option: CourseRelevantExecution | null): string {
        if (this.isExecutionSelectable(option)) {
            return '';
        }

        const disabledReason = option.selectionStatus?.disabledReason ?? this.resolveDisabledReasonFromStatus(option.status);

        if (disabledReason === CourseRelevantExecutionDisabledReasonEnum.RegistrationClosed) {
            return 'PAGE.DIALOG.ADD_PROGRAM_PARTICIPANTS.LABEL.EXECUTION_SUFFIX_CLOSED';
        }

        if (disabledReason === CourseRelevantExecutionDisabledReasonEnum.ExecutionPlanned) {
            return 'PAGE.DIALOG.ADD_PROGRAM_PARTICIPANTS.LABEL.EXECUTION_SUFFIX_PLANNED';
        }

        if (disabledReason === CourseRelevantExecutionDisabledReasonEnum.CourseListed) {
            return 'PAGE.DIALOG.ADD_PROGRAM_PARTICIPANTS.LABEL.EXECUTION_SUFFIX_CATALOG_BOOKING_REQUIRED';
        }

        return '';
    }

    private resolveDisabledReasonFromStatus(status: string | null | undefined): CourseRelevantExecutionDisabledReasonEnum | null {
        const normalizedStatus = status?.toLowerCase();

        if (normalizedStatus === ModuleStatuses.PLANNED) {
            return CourseRelevantExecutionDisabledReasonEnum.ExecutionPlanned;
        }

        if (normalizedStatus === AddProgramParticipantsDialogComponent.LISTING_REGISTRATION_CLOSED_STATUS) {
            return CourseRelevantExecutionDisabledReasonEnum.RegistrationClosed;
        }

        return null;
    }

    private getRelevantExecutionsContext(): string {
        return this.isCourseCatalogFeatureEnabled ? AddProgramParticipantsDialogComponent.BOOKING_CONTEXT : null;
    }

    async loadData() {
        this.programStructuresWithGroups = await this._programItemService.fetchProgramExecutionStructureWithDefaultExecutions(this.entityId);
        this.programStructuresWithGroups = this.programStructuresWithGroups.filter((programStructureWithGroup) => programStructureWithGroup.structure.length > 0);
        this.setFormControls();
    }

    setFormControls(): void {
        const controlArray = this.form.get('structures') as FormArray;

        this.programStructuresWithGroups.forEach((group) => {
            group.structure.forEach((structure) => {
                if (structure.defaultExecution) {
                    controlArray.push(new FormControl(structure.defaultExecution.id));
                    structure.options = [structure.defaultExecution];
                } else {
                    controlArray.push(new FormControl());
                    structure.options = [];
                }
                structure.isLoading = false;
            });
        });
    }

    getFormControl(index: number, j: number): FormControl | null {
        const controlArray = this.form.get('structures') as FormArray;

        if (!controlArray) {
            return null;
        }

        let flatIndex = 0;
        for (let i = 0; i < index; i++) {
            flatIndex += this.programStructuresWithGroups[i].structure.length;
        }

        flatIndex += j;

        if (flatIndex >= 0 && flatIndex < controlArray.length) {
            return controlArray.at(flatIndex) as FormControl;
        } else {
            return null;
        }
    }

    goForward(stepper: any): void {
        if (stepper.selectedIndex < stepper.steps.length - 1) {
            stepper.next();
        }
    }

    async onStepChange(stepper: MatStepper): Promise<void> {
        const currentIndex = this.showFirstStep ? stepper.selectedIndex : stepper.selectedIndex + 1;

        if (currentIndex === 0) {
            this.title = this._translateService.instant('PAGE.DIALOG.ADD_PROGRAM_PARTICIPANTS.LABEL.CHOOSE_PARTICIPANTS');
        } else if (currentIndex === 1) {
            this.title = this.moduleUser
                ? this._translateService.instant('PAGE.DIALOG.ADD_PROGRAM_PARTICIPANTS.LABEL.MANAGE_EXECUTIONS_FOR_PARTICIPANT', { fullName: this.moduleUser.user.fullName })
                : this._translateService.instant('PAGE.DIALOG.ADD_PROGRAM_PARTICIPANTS.LABEL.MANAGE_EXECUTIONS', { participantsCount: this.list.length });
        } else if (currentIndex === 2) {
            this.title = 'GENERAL.LABEL.CONFIRMATION';

            const payload = {
                courseExecutionIds: this.selectedExecutionIds,
                userIds: this.list.map((user) => user.id),
                programExecutionId: this.entityId,
            };

            if (this.selectedExecutionIds.length) {
                this.isProgramStructuresWithGroupsLoading = true;

                this.list.forEach((user) => {
                    this.selectedExecutionIds.forEach((courseId) => {
                        this.bookingsSet.add(`${user.id}-${courseId}`);
                    });
                });

                this.participantsExecutionChanges = await this._programItemService.fetchParticipantExecutionChanges(payload);

                const conflicts = this.participantsExecutionChanges.map((conflict) => {
                    return `${conflict.participant.id}-${conflict.selected.id}`;
                });

                conflicts.forEach((conflictKey) => {
                    if (this.bookingsSet.has(conflictKey)) {
                        this.bookingsSet.delete(conflictKey);
                    }
                });

                this.isProgramStructuresWithGroupsLoading = false;
            }
        }
    }

    removeFromList(user: User): void {
        this.list = [...this.list.filter((item) => item.id !== user.id)];

        this.dataSource = new AllUserDataSource(
            this.entitie$.pipe(
                map((users) => {
                    return users.map((item) => {
                        if (item.id === user.id) {
                            item = Object.assign(new User(), item, {
                                selected: false,
                            });
                        } else if (this.list.find((item2) => item2.id === item.id)) {
                            item = Object.assign(new User(), item, {
                                selected: true,
                            });
                        }
                        return item;
                    });
                })
            ),
            this._store,
            this._action$
        );
    }

    addToList(user: User): void {
        this.list = [...this.list, user];
        this.dataSource = new AllUserDataSource(
            this.entitie$.pipe(
                map((users) => {
                    return users.map((item) => {
                        if (item.id === user.id || this.list.find((item2) => item2.id === item.id)) {
                            item = Object.assign(new User(), item, {
                                selected: true,
                            });
                        }
                        return item;
                    });
                })
            ),
            this._store,
            this._action$
        );
    }

    onBookingToggleChange(executionChange: ParticipantExecutionChange, isSelected: boolean): void {
        if (isSelected) {
            this.bookParticipant(executionChange);
            return;
        }

        this.unbookParticipant(executionChange);
    }

    bookParticipant(executionChange: ParticipantExecutionChange): void {
        executionChange.selected.isSelected = true;

        this.bookingsSet.add(`${executionChange.participant.id}-${executionChange.selected.id}`);
    }

    unbookParticipant(executionChange: ParticipantExecutionChange): void {
        executionChange.selected.isSelected = false;
        this.bookingsSet.delete(`${executionChange.participant.id}-${executionChange.selected.id}`);
    }

    editProgramStructure(): void {
        this.dialogRef.close(false);
        this._store.dispatch(new Navigate([`/modules/${this.entityId}/edit/program-structure`]));
    }

    saveSelected(): void {
        // Convert the set to a list of objects
        const bookings = Array.from(this.bookingsSet).map((key) => {
            const [userId, executionId] = key.split('-');
            return { user_id: Number(userId), execution_id: Number(executionId) };
        });

        this.dialogRef.close({ userList: this.list, bookings: bookings, selectedModuleIds: this.selectedExecutionIds });
    }

    confirm(user: User): void {
        this.onConfirm.next(user);
    }

    async loadUsersPage(resetPageIndex = false, isUnassignedSelected = false): Promise<void> {
        if (resetPageIndex) {
            this.paginator.pageIndex = 0;
        }

        if (this.dataSource) {
            this.dataSource.loadUsers(
                this.dataType,
                [],
                this.entityId,
                this.searchInput.value,
                this.sort.active,
                this.sort.direction,
                this.paginator.pageSize,
                this.paginator.pageIndex + 1,
                isUnassignedSelected,
                this.role,
                false,
                this.moduleId
            );
        }
    }
}
